import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/aircraft", label: "Aircraft" },
  { to: "/insights", label: "Insights" },
  { to: "/financing", label: "Financing" },
  { to: "/contact", label: "Contact" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const onDarkHero = loc.pathname === "/" && !scrolled;

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 30);
    on();
    window.addEventListener("scroll", on);
    return () => window.removeEventListener("scroll", on);
  }, []);
  useEffect(() => { setOpen(false); }, [loc.pathname]);

  const linkIdle = onDarkHero ? "text-white/85 hover:text-white" : "text-[#445060] hover:text-[#0E1B2E]";
  const linkActive = onDarkHero ? "text-[#E4C79B]" : "text-[#A5854C]";
  const nameColor = onDarkHero ? "text-white" : "text-[#0E1B2E]";
  const tagColor = onDarkHero ? "text-[#E4C79B]/90" : "text-[#A5854C]";

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background,backdrop-filter,border-color] duration-500 ${
        scrolled ? "backdrop-blur-2xl bg-white/85 border-b border-[#0E1B2E]/10" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 h-[88px] flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <img
            src={onDarkHero ? "/logo-eagle-white.png" : "/logo-eagle.png"}
            alt="JS Aviation Group"
            className="h-11 w-auto shrink-0"
          />
          <div className="leading-tight">
            <div className={`font-serif text-[22px] md:text-[24px] tracking-tight transition-colors ${nameColor}`}>JS Aviation Group</div>
            <div className={`hidden md:block font-mono text-[10px] uppercase tracking-[0.22em] transition-colors ${tagColor}`} data-testid="nav-tagline">
              Your Trusted Partner · Connecting Aviation · Creating Value
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-4 py-2 text-[13px] tracking-tight transition-colors duration-300 ${
                  isActive ? linkActive : linkIdle
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <Link
            to="/contact"
            data-testid="nav-cta-consult"
            className="btn-gold text-[12px] uppercase tracking-[0.2em] font-mono px-5 py-3"
          >
            Schedule Consultation
          </Link>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className={`lg:hidden p-2 transition-colors ${onDarkHero ? "text-white" : "text-[#0E1B2E]"}`}
          onClick={() => setOpen((v) => !v)}
          aria-label="Menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-white border-b border-[#0E1B2E]/10"
          >
            <div className="px-6 py-6 flex flex-col gap-3">
              {links.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-mobile-${l.label.toLowerCase()}`}
                  className="font-serif text-2xl tracking-tight text-[#0E1B2E]"
                >
                  {l.label}
                </Link>
              ))}
              <Link
                to="/contact"
                className="btn-gold text-[12px] uppercase tracking-[0.2em] font-mono px-5 py-3 mt-4 inline-block w-fit"
                data-testid="nav-mobile-cta"
              >
                Schedule Consultation
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
